<template>
  <div v-if="!successfulSubmission" :class="$style['application-form']">
    <div :class="$style['application-form__wrapper']">
      <h3 class="h-margin-bottom-l">
        {{ $t('job-application/form-title') }}
      </h3>
      <FormInput
        id="fullname"
        v-model="application.name"
        data-test-id="firstname"
        type="text"
        :validation="v$.application.name"
        validate
        :label="`${$t('account/full-name')} *`"
        autocomplete="given-name" />

      <div :class="$style['application-form__container']">
        <FormDate
          v-model="application.birthday"
          class="h-margin-bottom-m"
          type="date"
          :validation="v$.application.birthday"
          :sublabel="$t('general/shorthand-example') + ' 12-04-1997'"
          :label="$t('account/birthday')" />
        <Dropdown
          :validation="v$.application.gender"
          :selected="genders.filter(item => item.value === application.gender)[0]"
          bordered
          required
          :class="$style['application-form__gender-dropdown']"
          :items="genders"
          @input="item => application.gender = item.value">
          <template
            v-if="application.gender"
            #selected>
            {{ genders.filter(item => item.value === application.gender)[0].name }}
          </template>
          <template v-else #selected>
            {{ $t('general/gender') }}
          </template>
          <template #item="{ item }">
            <slot name="item" :item="item">
              {{ item.name }}
            </slot>
          </template>
        </Dropdown>
      </div>

      <FormInput
        id="email"
        v-model="application.email"
        data-test-id="email"
        validate
        :validation="v$.application.email"
        type="email"
        :label="`${$t('general/emailaddress')} *`"
        autocomplete="email" />

      <div>
        <FormPhone
          id="phone"
          v-model="application.phone"
          data-test-id="phone"
          :country="application.country"
          :validation="v$.application.phone"
          :label="
            $t('account/phonenumber') + ' *'
          " />
        <span class="h-font-size-xs h-color-grey">{{ $t('checkout/for-shipping-related-questions') }}</span>
      </div>

      <FormCountry
        id="country"
        v-model="application.country"
        data-test-id="country"
        :validation="v$.application.country"
        validate
        :label="`${$t('account/country')} *`"
        autocomplete="-1" />

      <FormInput
        id="city"
        v-model="application.city"
        data-test-id="city"
        validate
        :validation="v$.application.city"
        :label="`${$t('account/woonplaats')} *`" />
      <div v-if="jobData.days.length" :class="$style['application-form__days-container']">
        <span class="h-font-size-xs h-color-grey">
          {{ $t('general/filter-available') }}
        </span>
        <FormCheckbox
          v-for="(day, index) in jobData.days"
          :key="day"
          :target-value="1"
          :v-bind="day"
          :hide-validation-text="index !== jobData.days.length - 1"
          :validation="v$.application.days"
          :label="day"
          @update:model-value="value => updateDays(day, value)" />
      </div>
      <FormInput
        id="hours"
        v-model="application.hours"
        data-test-id="hours"
        :maxlength="32"
        validate
        :validation="v$.application.hours"
        :label="$t('job-application/validation/hours')" />

      <FormInput
        id="motivation"
        v-model="application.motivation"
        :label="$t('job-application/validation/motivation')"
        draggable
        :validation="v$.application.motivation"
        :size="4" />

      <FormUpload
        v-model="application.motivation_file"
        :label="$t('job-application/validation/motivation_file')"
        :validation="v$.application.motivation_file"
        :accept="`image/*,application/pdf`" />

      <FormUpload
        v-model="application.attached_file"
        required
        :label="$t('job-application/validation/attached_file')"
        :validation="v$.application.attached_file"
        :accept="`image/*,application/pdf`" />

      <FormCheckbox
        v-model="application.acceptConditions"
        :target-value="1"
        :checked="application.acceptConditions"
        :validation="v$.application.acceptConditions"
        class="h-margin-bottom">
        <span>{{ $t('vacancies/privacy-checkbox') }} <NuxtLink :to="{name: `${$lang}-vacancies-privacy`}">Privacy statement</NuxtLink></span>
      </FormCheckbox>

      <Btn
        color="cta"
        :loading="loading"
        @click="submitApplication()">
        {{ $t('vacancies/apply-now') }}
      </Btn>
    </div>
  </div>
  <div v-else :class="$style['application-form__success']" class="container--tiny container--no-padding container container--inline">
    <p>{{ $t('vacancies/successful-submission') }}</p>
  </div>
</template>
<script setup>
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email, helpers } from '@vuelidate/validators'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { isValidName as isValidNameValidator } from '~/helpers/custom-validators'
import { birthday as birthdayValidationRule } from '~/helpers/validation-rules'

await useI18n(['general', 'vacancies', 'account', 'job-application'])

const { $lang } = useNuxtApp()
const { showAlert } = useDialog()
const { $api } = useNuxtApp()

const application = ref({
  name: null,
  gender: null,
  phone: null,
  email: null,
  country: null,
  city: null,
  days: [],
  hours: null,
  acceptConditions: null,
  motivation: null,
  motivation_file: null,
  attached_file: null,
  birthday: null
})

const genders = [
  { value: 'm', name: $t('general/man') },
  { value: 'f', name: $t('general/woman') },
  { value: 'o', name: $t('general/other') }
]

const successfulSubmission = ref(false)
const loading = ref(false)

const emit = defineEmits(['validate', 'update:modelValue', 'is-dirty'])

const props = defineProps({
  modelValue: {
    type: [Object, Array, Boolean],
    default: null
  },
  hideEmail: {
    type: Boolean,
    default: false
  },
  jobData: {
    type: Object,
    default: null
  }
})

const rules = {
  application: {
    name: {
      required,
      isValidName: helpers.withParams(
        { type: 'isValidName' },
        isValidNameValidator
      )
    },
    email: {
      required,
      email
    },
    city: {
      required
    },
    country: {
      required
    },
    birthday: birthdayValidationRule,
    gender: {
      required
    },
    phone: {
      required,
      isValidPhone (value, vm) {
        if (!value && !phoneRequired.value) { return true }
        // if (this.hidePhone) return true
        if (!vm.country) { return false }
        try {
          return isValidPhoneNumber(value) ? 1 : 0
        } catch (e) {
          return 0
        }
      }
    },
    hours: {
      required
    },
    days: {
      required
    },
    motivation: {
      required: requiredIf(() => !application.value.motivation_file)
    },
    motivation_file: {
      required: requiredIf(() => !application.value.motivation)
    },
    attached_file: {
      required
    },
    acceptConditions: {
      required
    }
  }
}

const updateDays = (day, value) => {
  if (value) {
    application.value.days.push(day)
  } else {
    application.value.days = application.value.days.filter(item => item !== day)
  }
}

const submitApplication = async () => {
  loading.value = true
  v$.value.$validate()
  const formData = new FormData()
  for (const key in application.value) {
    if (key === 'days') {
      for (let i = 0; i < application.value[key].length; i++) {
        formData.append('days[]', application.value[key][i])
      }
    } else {
      if (key === 'motivation_file' && application.value[key] === null) {
        continue
      }
      formData.append(key, application.value[key])
    }
  }
  if (v$.value.$invalid) {
    loading.value = false
    return false
  }

  const errors = ref(null)
  try {
    await $api(`/job-applications/${props.jobData.id}`,
      {
        method: 'POST',
        body: formData,
        recaptchaKey: 'job_applications'
      })
  } catch (e) {
    if (e?.response?._data?.errors) {
      errors.value = e.response._data.errors
    }
  }
  if (errors.value === null) {
    successfulSubmission.value = true
    loading.value = false
  }

  if (errors.value) {
    showAlert(errors.value[0].message ? errors.value[0].message : 'something went wrong, please try again later')
    loading.value = false
    return false
  }
}

const v$ = useVuelidate(rules, { application })

const dirty = computed(() => {
  return v$.value.$anyDirty
})
const valid = computed(() => {
  return !v$.value.$invalid
})
const phoneRequired = computed(() => {
  const optionalCountries = ['NL', 'DE', 'BE']
  const isRequired = !optionalCountries.includes(application.value?.country)
  return isRequired
})

watch(valid, isValid => {
  emit('validate', isValid)
})

watch(dirty, isDirty => {
  emit('is-dirty', isDirty)
})

onMounted(() => {
  if (props.modelValue) {
    application.value = { ...props.modelValue }
  }
  if (!application.value.country) {
    application.value.country =
          $lang.toUpperCase() === 'EN'
            ? 'NL' // Hail mary to nl. This only happens when there is not predicted country from the cart-update call.
            : $lang.toUpperCase()
  }

  emit('validate', valid.value)
})

</script>
<style lang="scss" module>
.application-form {
  border-radius: $border-radius;
  box-shadow: $box-shadow-large;
  margin-top: $spacing * 4;

  &__wrapper {
    position: relative;
    z-index: 1;
    max-width: 700px;
    margin: 0 auto;
    padding-block: $spacing * 10;
    padding-inline: $spacing * 3;
    display: flex;
    flex-direction: column;
    gap: $spacing * 2;
    border-radius: $border-radius;

    @media (max-width: #{$breakpoint-portrait-tablet}) {
      padding-block: $spacing * 4;
    }
  }

  &__container {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap : $spacing * 2;

    > * {
      max-width: 50%;
    }

    @media (max-width: #{$breakpoint-mobile}) {
      flex-direction: column;
      gap: 0;

      > * {
        max-width: unset;
        width: 100%;
      }
    }
  }

  &__gender-dropdown {
    height: $spacing * 6;
    margin-top: $spacing * 4.2;
    flex: 1;
    background-color: white;

    @media (max-width: #{$breakpoint-mobile}) {
      margin-top: 0;
      flex: unset;
    }
  }

  &__country-dropdown {
    background-color: white;
  }

  &__days-checkbox {
    .checkbox-validation {
      background-color: red;
    }
  }

  &__success {
    margin: $spacing * 2 auto;
    padding: $spacing * 3 $spacing * 2;
    background-color: $grey-lightest;
    border-radius: $border-radius;
    text-align: center;

    p {
      margin: 0;
    }
  }
}
</style>
